import type { FC, ReactNode } from "react";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import type { ChipStatus } from "../../types/chip";
import { FilterChipVariant } from "../../types/commonEnums";
import { FiltersChipRemovedTooltip } from "../FiltersChipRemovedTooltip/FiltersChipRemovedTooltip";

import { StyledChipContainer } from "./styles";
import { renderChip } from "./utils";

type FiltersChipProps = {
    id?: number | string;
    code?: string;
    iconName?: IconName;
    text: string;
    variant?: FilterChipVariant;
    chipStatus?: ChipStatus;
    hideActionButton?: boolean;
    iconSrc?: string;
    maxChipTextSize?: number;
    tooltipLines?: readonly string[];
    onClose?: () => void;
};

const DEFAULT_MAX_CHIP_TEXT_SIZE = 29;
const CHIP_TOOLTIP_MAX_HEIGHT = "256px";

const renderTooltipContent = (text: string, tooltipLines?: readonly string[]): ReactNode => {
    if (tooltipLines && tooltipLines.length > 0) {
        return (
            <Box
                maxHeight={CHIP_TOOLTIP_MAX_HEIGHT}
                overflowY="auto"
            >
                <Stack gap="4px">
                    {tooltipLines.map((line, index) => (
                        <Text
                            key={`${line}-${index}`}
                            fontVariant="body2"
                            color={colorsPalettes.carbon[0]}
                        >
                            {line}
                        </Text>
                    ))}
                </Stack>
            </Box>
        );
    }

    return text;
};

export const FiltersChip: FC<FiltersChipProps> = ({
    id,
    code,
    iconName,
    text,
    iconSrc,
    variant = FilterChipVariant.Default,
    chipStatus = "added",
    hideActionButton = false,
    maxChipTextSize = DEFAULT_MAX_CHIP_TEXT_SIZE,
    tooltipLines,
    onClose,
}) => {
    const isChipRemoved = chipStatus === "removed";

    const basicChipElement = renderChip(variant, {
        id,
        code,
        iconName,
        text: text.length > maxChipTextSize ? `${text.slice(0, maxChipTextSize)}...` : text,
        onCloseItem: () => onClose?.(),
        hideActionButton: hideActionButton || isChipRemoved,
        image: iconSrc,
        isCompact: true,
    });

    const chipContent = [FilterChipVariant.Country, FilterChipVariant.State].includes(variant) ? (
        <StyledChipContainer>{basicChipElement}</StyledChipContainer>
    ) : (
        basicChipElement
    );

    if (isChipRemoved) {
        return <FiltersChipRemovedTooltip>{chipContent}</FiltersChipRemovedTooltip>;
    }

    const tooltipContent = renderTooltipContent(text, tooltipLines);
    const shouldShowTooltip = (tooltipLines && tooltipLines.length > 0) || text.length > maxChipTextSize;

    if (shouldShowTooltip) {
        return (
            <Tooltip content={tooltipContent}>
                <div>{chipContent}</div>
            </Tooltip>
        );
    }

    return chipContent;
};
